import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Polygon, MultiPolygon, Geometry } from 'ol/geom';
import { Fill, Stroke, Style} from 'ol/style';
import { WhaleZonesModel } from '../models/whale-zones-model';
import { Feature } from 'ol';
import { ZoneFeature } from '../models/zones-base-interfaces';
import { formatFromTo, getToolTipText } from './utils';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface WhaleZonesColor
{
    borderColor: string;
    fillColor: string;
    avoid: string;
    restricted: string;
}

export abstract class WhaleZonesLayerBase extends EventTarget
{
    abstract connectEventHandlers(): void;
    abstract getZones(): Promise<ZoneFeature[] | null>;

    private _whaleZonesLayer : VectorLayer<VectorSource<Geometry>> | null = null;

    constructor(colors?: Partial<WhaleZonesColor>)
    {
        super();
        this.setColors(colors || {});
    }

    get colors(): WhaleZonesColor
    {
        return this._colors;
    }

    get layers(): VectorLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        this.connectEventHandlers();
        this._vectorSource = new VectorSource();
        this._whaleZonesLayer = new VectorLayer({source: this._vectorSource, visible: false});
        this._layers.push(this._whaleZonesLayer);
        this.plotWhaleZones();
    }

    setColors(colors: Partial<WhaleZonesColor>): void
    {
        this.colors.borderColor = colors.borderColor || this.colors.borderColor;
        this.colors.fillColor = colors.fillColor || this.colors.fillColor;

        if (this._vectorSource){
            this._clear();
            this.plotWhaleZones();
        }
    }

    private _clear()
    {
        try {
            this._vectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    enable() {
        this._clear();
        this.plotWhaleZones().then(() => this._whaleZonesLayer!.setVisible(true));
    }

    disable() {
        this._whaleZonesLayer!.setVisible(false);
        this._clear();
    }

    private _colors: WhaleZonesColor = {
        borderColor: "black",
        fillColor: "black",
        avoid: "yellow",
        restricted: "red"
    };

    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    private _vectorSource: VectorSource<Geometry>|null = null;

    // coordinates crossing the IDL need to be normalized
    private normalizeCoordinates = (coords: number[][]): number[][] => {
            if (coords.length < 2) return coords;
            
            const normalized = [coords[0]];
            
            for (let i = 1; i < coords.length; i++) {
                const prev = normalized[i - 1];
                const curr = coords[i];
                
                let [lon, lat] = curr;
                const prevLon = prev[0];
                
                // if longitude jump is > 180 degrees, adjust it
                const lonDiff = lon - prevLon;
                if (lonDiff > 180) {
                    lon -= 360;
                } else if (lonDiff < -180) {
                    lon += 360;
                }
                
                normalized.push([lon, lat]);
            }
            
            return normalized;
    };

    private normalizePolygonCoords = (coords: number[][][]): number[][][] => {
        return coords.map(ring => this.normalizeCoordinates(ring));
    };

    private normalizeMultiPolygonCoords = (coords: number[][][][]): number[][][][] => {
        return coords.map(polygon => this.normalizePolygonCoords(polygon));
    };

    public async plotWhaleZones() {
        this._clear();

        const whaleZoneFeatures = await this.getZones();
        if (!whaleZoneFeatures) {
            return;
        }

        const features: Feature<Geometry>[] = [];
        const fillColor = this.colors.fillColor || "black";
        const borderColor = this.colors.borderColor || "black";
        const restricedColor = this.colors.restricted || "red";

        const zoneStyle = new Style({
            stroke: new Stroke({
                color: borderColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });

        const zoneStyleRestricted = new Style({
            stroke: new Stroke({
                color: restricedColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });

        whaleZoneFeatures!.forEach(z => {
            let feature: Feature<Geometry>;
            if (z.geofence!.type === "Polygon") {
                const normalizedCoords = this.normalizePolygonCoords(<number[][][]>z.geofence!.coordinates);
                feature = new Feature({geometry: new Polygon(normalizedCoords)});
            }
            if (z.geofence!.type === "MultiPolygon") {
                const normalizedCoords = this.normalizeMultiPolygonCoords(<number[][][][]>z.geofence!.coordinates);
                feature = new Feature({geometry: new MultiPolygon(normalizedCoords)});
            }

            let style = zoneStyle;
            
            if(z.properties && z.properties[0]){
                if (z.properties[0]?.isNoGoZone) {
                    style = zoneStyleRestricted;
                }
            }

            const text = getToolTipText(z);
            feature!.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            feature!.setStyle(style);
            feature!.set('text', text);
            feature!.set('layer', "WhaleZonesLayer");
            features.push(feature!);
    })      

    this._vectorSource!.addFeatures(features);
}
};
